import { useEffect, Fragment, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import CheckIcon from "@mui/icons-material/Check";
// import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import EditIcon from "@mui/icons-material/Edit";
import AppsIcon from "@mui/icons-material/Apps";

type Sector = {
  name: string;
  value: string;
  isCustom?: boolean;
};

interface DomainDropdownProps {
  selected: Sector;
  setSelected: (sector: Sector) => void;
}

const sectorOptions: Sector[] = [
  { name: "Select Sector", value: "" },
  { name: "Manufacturing", value: "Manufacturing" },
  { name: "Retail", value: "Retail" },
  { name: "Healthcare", value: "Healthcare" },
  { name: "AgriTech", value: "AgriTech" },
  { name: "Finance", value: "Finance" },
  { name: "Education", value: "Education" },
  { name: "Logistics", value: "Logistics" },
  { name: "Enter Custom Sector", value: "custom", isCustom: true },
];

export default function DomainDropdown({ selected, setSelected }: DomainDropdownProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [customValue, setCustomValue] = useState("");

  // Clear local state when parent resets
  useEffect(() => {
    if (selected.value === "" && !selected.isCustom) {
      setCustomValue("");
      setIsEditing(false);
    }
  }, [selected]);

  const handleCustomInput = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (customValue.trim()) {
      setSelected({
        name: customValue.trim(),
        value: customValue.trim().toLowerCase(),
        isCustom: true,
      });
      setIsEditing(false);
      setCustomValue("");
    }
  };

  return (
    <div className="relative mt-1">
      {isEditing ? (
        <form onSubmit={handleCustomInput} className="relative">
          <input
            type="text"
            value={customValue}
            onChange={(e) => {
              setCustomValue(e.target.value);
              setSelected({
                name: e.target.value,
                value: e.target.value.toLowerCase(),
                isCustom: true,
              });
            }}
            className="w-full md:w-[240px] h-14 px-5 pr-8 py-2 bg-[#0F0F0F]/80 border border-[#1F1F1F] text-white rounded-[12px] focus:outline-none"
            placeholder="Enter custom sector"
            autoFocus
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <button type="button" onClick={() => setIsEditing(false)} className="text-red-400 hover:text-red-300 p-1">
              ✕
            </button>
          </div>
        </form>
      ) : (
        <Listbox value={selected} onChange={(value) => {
          if (value.isCustom) {
            setIsEditing(true);
          } else {
            setSelected(value);
          }
        }}>
          {({ open }) => (
            <div className={`relative ${open ? "z-[200]" : "z-10"}`}>
              <Listbox.Button className="relative w-full md:w-[240px] h-14 cursor-pointer flex items-center justify-between text-white rounded-[12px] pl-5 pr-2 py-2 focus:outline-none bg-transparent border border-[#1F1F1F]">
                <span className="flex items-center gap-2">
                  {selected.value === ""
                    ? <AppsIcon style={{ fontSize: 18, color: "#94a3b8" }} />
                    : (selected.isCustom && <EditIcon className="h-4 w-4" />)}
                  {selected.name}
                </span>

                <span
                  className={`pointer-events-none ml-2 inline-flex items-center justify-center rounded-[135px] w-14 h-10 ${open ? "bg-[#232323]" : "bg-[#1A1A1A]"}`}
                >
                  <svg width="12" height="7.4" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                    <path d="M1 2l5 4 5-4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </Listbox.Button>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options className="absolute mt-1 w-full md:w-[240px] max-h-50 overflow-y-auto bg-[#0F0F0F] border border-[#1F1F1F] rounded-[12px] shadow-lg">
                  {sectorOptions.map((sector, idx) => (
                    <Listbox.Option
                      key={idx}
                      className={({ active }) =>
                        `cursor-pointer select-none relative py-2 pl-4 pr-10 ${
                          active ? "bg-white/5 text-white" : "text-gray-200"
                        }`
                      }
                      value={sector}
                    >
                      {({ selected }) => (
                        <span className="flex items-center gap-2">
                          {sector.isCustom && <EditIcon className="h-4 w-4" />}
                          {sector.name}
                          {selected && !sector.isCustom && (
                            <span className="absolute inset-y-0 right-4 flex items-center text-gray-300">
                              <CheckIcon style={{ fontSize: 22 }} />
                            </span>
                          )}
                        </span>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </div>
          )}
        </Listbox>
      )}
    </div>
  );
}